import type { CSSProperties } from 'react';
import { nivelDoMedidor } from './nivel-do-medidor';
import './medidor.css';

/**
 * O medidor: quanto de um limite já foi usado (#765).
 *
 * Usuários e armazenamento, na lista de organizações, no resumo do detalhe e
 * em Pessoas. O texto vem pronto de quem usa ("9,3 de 10 GB"), porque a unidade
 * e o arredondamento são da tela, e não do medidor.
 *
 * ── A cor não fala sozinha ────────────────────────────────────────────────
 *
 * A partir de 90% a barra fica mostarda e a porcentagem aparece ao lado do
 * texto; no limite, laranja e "no limite". A palavra diz o mesmo que a cor,
 * para quem não distingue as cores e para o leitor de tela, que lê o
 * `aria-valuetext`.
 *
 * ── O movimento ───────────────────────────────────────────────────────────
 *
 * A barra enche ao aparecer, com atraso pela `posicao` na lista (até a 10ª).
 * Quando o valor muda, quem usa troca a `key` e ela enche de novo, do zero,
 * como no design system da #765.
 */
export type MedidorProps = {
  usado: number;
  total: number;
  /** O uso já escrito, com a unidade: "9,3 de 10 GB". */
  rotulo: string;
  /** O que o medidor mede, para o leitor de tela: "Armazenamento". */
  nome: string;
  /** A posição na lista, a partir de 0: atrasa a entrada, até a 10ª. */
  posicao?: number;
};

export function Medidor({ usado, total, rotulo, nome, posicao }: MedidorProps) {
  const { nivel, fracao, marca } = nivelDoMedidor(usado, total);
  return (
    <div
      className={`medidor medidor--${nivel}`}
      role="meter"
      aria-label={nome}
      aria-valuemin={0}
      aria-valuemax={Math.max(total, 0)}
      aria-valuenow={Math.min(Math.max(usado, 0), Math.max(total, 0))}
      aria-valuetext={marca ? `${rotulo}, ${marca}` : rotulo}
      style={posicao === undefined ? undefined : ({ '--i': posicao } as CSSProperties)}
    >
      <span className="medidor__texto" aria-hidden="true">
        {rotulo}
        {marca ? <em className="medidor__marca">{marca}</em> : null}
      </span>
      <span className="medidor__barra" aria-hidden="true">
        <span className="medidor__cheio" style={{ '--v': fracao } as CSSProperties} />
      </span>
    </div>
  );
}
